import React, { useState, useEffect, useCallback } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer';
import Newsletter from '../components/Newsletter';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronRight, faUser, faClipboardList, faEnvelope, faPhone, faLock,
    faPen, faEye, faEyeSlash, faFileInvoice, faClock, faChevronLeft,
    faChevronDown, faTachometerAlt, faEdit, faPlus, faList, faDollarSign,
    faLayerGroup, faUpload, faTimes, faCheckCircle, faRuler, faPalette,
    faFont, faFileAlt, faLink, faImage, faTag, faStar, faCode, faGlobe,
    faSpinner, faExclamationCircle, faHistory,
} from "@fortawesome/free-solid-svg-icons";
import {
    fetchDashboardStats, fetchOrders, submitOrder,
    fetchEdits, submitEdit, updateProfile, changePassword,
} from '../services/dashboardService';
import { getUser, logout } from '../services/authService';

/* ─── Constants ──────────────────────────────────────────────────────────── */

const SERVICE_ICONS = {
    "Embroidery Digitizing": "🧵",
    "Vectorization": "✏️",
    "Website Development": "💻",
    "Logo Designing": "🎨",
    "Brandings": "🏷️",
};

const SERVICES = ["Embroidery Digitizing", "Vectorization", "Website Development", "Logo Designing", "Brandings"];

const CURRENCIES = [
    { code: "USD", symbol: "$", flag: "🇺🇸" },
    { code: "GBP", symbol: "£", flag: "🇬🇧" },
];

const USD_TO_GBP = 0.79;

const STATUS_STYLES = {
    pending:     { bg: "#fff8e1", color: "#d97706", label: "Pending"     },
    in_progress: { bg: "#e0f0ff", color: "#1d6a88", label: "In Progress" },
    completed:   { bg: "#e6f9f0", color: "#0e8a5f", label: "Completed"   },
    cancelled:   { bg: "#fef2f2", color: "#dc2626", label: "Cancelled"   },
};

/* ─── Shared UI helpers ───────────────────────────────────────────────────── */

function Spinner() {
    return (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 28, color: '#1d6a88' }} />
        </div>
    );
}

function ErrorMsg({ msg }) {
    if (!msg) return null;
    return (
        <div className="udash-error-banner">
            <FontAwesomeIcon icon={faExclamationCircle} /> {msg}
        </div>
    );
}

function SuccessMsg({ msg }) {
    if (!msg) return null;
    return (
        <div className="udash-success-banner">
            <FontAwesomeIcon icon={faCheckCircle} /> {msg}
        </div>
    );
}

function Field({ label, icon, children }) {
    return (
        <div className="udash-field">
            {label && <label>{icon && <FontAwesomeIcon icon={icon} />} {label}</label>}
            {children}
        </div>
    );
}

function SelectField({ label, icon, options, placeholder, value, onChange, name }) {
    return (
        <Field label={label} icon={icon}>
            <div className="udash-select-wrap">
                <select value={value || ''} onChange={onChange} name={name}>
                    {placeholder && <option value="" disabled>{placeholder}</option>}
                    {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <FontAwesomeIcon icon={faChevronDown} className="udash-select-arrow" />
            </div>
        </Field>
    );
}

function FileUploadField({ label, hint, id, files, setFiles }) {
    const handleFiles = (e) => setFiles(prev => [...prev, ...Array.from(e.target.files)]);
    const handleDrop  = (e) => { e.preventDefault(); setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]); };
    const removeFile  = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));
    return (
        <Field label={label}>
            <div className="udash-dropzone" onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => document.getElementById(id).click()}>
                <FontAwesomeIcon icon={faUpload} className="udash-dropzone-icon" />
                <p className="udash-dropzone-text">Drag & drop files, or <span>click to browse</span></p>
                <p className="udash-dropzone-hint">{hint || "Supports: JPG, PNG, PDF, AI, EPS — Max 20 MB each"}</p>
                <input id={id} type="file" multiple style={{ display: "none" }} onChange={handleFiles} />
            </div>
            {files.length > 0 && (
                <div className="udash-file-list">
                    {files.map((f, i) => (
                        <div className="udash-file-item" key={i}>
                            <FontAwesomeIcon icon={faFileInvoice} className="udash-file-icon" />
                            <span className="udash-file-name">{f.name}</span>
                            <span className="udash-file-size">{(f.size / 1024).toFixed(0)} KB</span>
                            <button className="udash-file-remove" type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </Field>
    );
}

function SectionLabel({ children }) {
    return <div className="udash-form-section-label">{children}</div>;
}

/* ─── Service Forms ───────────────────────────────────────────────────────── */

function useFormState(initial) {
    const [fields, setFields] = useState(initial);
    const set = (name, value) => setFields(prev => ({ ...prev, [name]: value }));
    const handleChange = (e) => set(e.target.name, e.target.value);
    return [fields, set, handleChange];
}

function EmbroideryDigitizingForm({ onFieldsChange }) {
    const [f, , hc] = useFormState({ fullName:'', email:'', placement:'', outputFormat:'', width:'', height:'', fabric:'', colors:'', stitchType:'', urgency:'', notes:'' });
    useEffect(() => onFieldsChange(f), [f]);
    return (
        <div className="udash-form-body">
            <SectionLabel>Client Details</SectionLabel>
            <div className="udash-form-row">
                <Field label="Full Name" icon={faUser}><input type="text" name="fullName" value={f.fullName} onChange={hc} placeholder="Your full name" /></Field>
                <Field label="Email Address" icon={faEnvelope}><input type="email" name="email" value={f.email} onChange={hc} placeholder="your@email.com" /></Field>
            </div>
            <SectionLabel>Design Specifications</SectionLabel>
            <div className="udash-form-row">
                <SelectField label="Placement / Garment Type" icon={faTag} name="placement" value={f.placement} onChange={hc} placeholder="— Select placement —" options={["Left Chest","Right Chest","Center Chest","Back (Full)","Back (Upper)","Sleeve","Cap Front","Cap Side","Collar","Pocket","Other"]} />
                <SelectField label="Output Format" icon={faFileAlt} name="outputFormat" value={f.outputFormat} onChange={hc} placeholder="— Select format —" options={["DST","PES","JEF","HUS","VP3","EXP","XXX","SEW","Multiple Formats"]} />
            </div>
            <div className="udash-form-row">
                <Field label="Design Width (inches)" icon={faRuler}><input type="number" name="width" value={f.width} onChange={hc} placeholder="e.g. 3.5" min="0.5" step="0.1" /></Field>
                <Field label="Design Height (inches)" icon={faRuler}><input type="number" name="height" value={f.height} onChange={hc} placeholder="e.g. 2.0" min="0.5" step="0.1" /></Field>
            </div>
            <div className="udash-form-row">
                <SelectField label="Fabric Type" icon={faTag} name="fabric" value={f.fabric} onChange={hc} placeholder="— Select fabric —" options={["Polo / Pique","T-Shirt / Jersey","Cap / Hat","Fleece","Denim","Towel / Terry","Leather","Nylon / Sports Wear","Other"]} />
                <SelectField label="Number of Colors" icon={faPalette} name="colors" value={f.colors} onChange={hc} placeholder="— Select colors —" options={["1 Color","2 Colors","3 Colors","4 Colors","5 Colors","6+ Colors"]} />
            </div>
            <div className="udash-form-row">
                <SelectField label="Stitch Type Preference" icon={faStar} name="stitchType" value={f.stitchType} onChange={hc} placeholder="— Select stitch type —" options={["Standard Fill","3D Puff","Satin Stitch","Run Stitch","Cross Stitch","Mix / Auto"]} />
                <SelectField label="Urgency / Turnaround" icon={faClock} name="urgency" value={f.urgency} onChange={hc} placeholder="— Select turnaround —" options={["Standard (24–48 hrs)","Rush (12 hrs)","Super Rush (6 hrs)"]} />
            </div>
            <Field label="Additional Notes / Color Instructions">
                <textarea name="notes" value={f.notes} onChange={hc} rows={3} placeholder="e.g. 'Use Madeira thread color #1234 for the outline'…" />
            </Field>
        </div>
    );
}

function VectorizationForm({ onFieldsChange }) {
    const [f, , hc] = useFormState({ fullName:'', email:'', outputFormat:'', intendedUse:'', colors:'', complexity:'', colorRefs:'', urgency:'', notes:'' });
    useEffect(() => onFieldsChange(f), [f]);
    return (
        <div className="udash-form-body">
            <SectionLabel>Client Details</SectionLabel>
            <div className="udash-form-row">
                <Field label="Full Name" icon={faUser}><input type="text" name="fullName" value={f.fullName} onChange={hc} placeholder="Your full name" /></Field>
                <Field label="Email Address" icon={faEnvelope}><input type="email" name="email" value={f.email} onChange={hc} placeholder="your@email.com" /></Field>
            </div>
            <SectionLabel>Vector Specifications</SectionLabel>
            <div className="udash-form-row">
                <SelectField label="Output Format" icon={faFileAlt} name="outputFormat" value={f.outputFormat} onChange={hc} placeholder="— Select format —" options={["AI (Adobe Illustrator)","EPS","SVG","PDF (Vector)","CDR (CorelDraw)","Multiple Formats"]} />
                <SelectField label="Intended Use" icon={faTag} name="intendedUse" value={f.intendedUse} onChange={hc} placeholder="— Select use —" options={["Print / Offset","Screen Printing","Embroidery","Laser Engraving","Vinyl Cutting","Web / Digital","General / All Purpose"]} />
            </div>
            <div className="udash-form-row">
                <SelectField label="Number of Colors" icon={faPalette} name="colors" value={f.colors} onChange={hc} placeholder="— Select colors —" options={["1 Color (B&W)","2 Colors","3 Colors","4 Colors","5 Colors","Full Color / Gradient"]} />
                <SelectField label="Complexity Level" icon={faStar} name="complexity" value={f.complexity} onChange={hc} placeholder="— Select complexity —" options={["Simple (text / basic shapes)","Medium (logo with icons)","Complex (detailed illustration)","Very Complex (portrait / photo)"]} />
            </div>
            <Field label="Color References / Pantone Codes" icon={faPalette}>
                <input type="text" name="colorRefs" value={f.colorRefs} onChange={hc} placeholder="e.g. PMS 286 C, #002536, Red, White, Black" />
            </Field>
            <SelectField label="Urgency / Turnaround" icon={faClock} name="urgency" value={f.urgency} onChange={hc} placeholder="— Select turnaround —" options={["Standard (24–48 hrs)","Rush (12 hrs)","Super Rush (6 hrs)"]} />
            <Field label="Additional Instructions">
                <textarea name="notes" value={f.notes} onChange={hc} rows={3} placeholder="e.g. 'Keep the gradient on the background'…" />
            </Field>
        </div>
    );
}

function WebsiteDevelopmentForm({ onFieldsChange }) {
    const [f, , hc] = useFormState({ fullName:'', email:'', phone:'', businessName:'', websiteType:'', pages:'', platform:'', hasDomain:'', hasHosting:'', deadline:'', referenceUrls:'', needsCms:'', needsSeo:'', description:'' });
    useEffect(() => onFieldsChange(f), [f]);
    return (
        <div className="udash-form-body">
            <SectionLabel>Client Details</SectionLabel>
            <div className="udash-form-row">
                <Field label="Full Name" icon={faUser}><input type="text" name="fullName" value={f.fullName} onChange={hc} placeholder="Your full name" /></Field>
                <Field label="Email Address" icon={faEnvelope}><input type="email" name="email" value={f.email} onChange={hc} placeholder="your@email.com" /></Field>
            </div>
            <div className="udash-form-row">
                <Field label="Phone Number" icon={faPhone}><input type="tel" name="phone" value={f.phone} onChange={hc} placeholder="+1 (555) 000-0000" /></Field>
                <Field label="Business / Company Name" icon={faTag}><input type="text" name="businessName" value={f.businessName} onChange={hc} placeholder="Your business name" /></Field>
            </div>
            <SectionLabel>Project Details</SectionLabel>
            <div className="udash-form-row">
                <SelectField label="Website Type" icon={faGlobe} name="websiteType" value={f.websiteType} onChange={hc} placeholder="— Select type —" options={["Business / Corporate","E-Commerce / Online Store","Portfolio / Personal","Blog / News","Landing Page","SaaS / Web App","Non-Profit","Restaurant / Food","Real Estate","Other"]} />
                <SelectField label="Number of Pages" icon={faFileAlt} name="pages" value={f.pages} onChange={hc} placeholder="— Select pages —" options={["1 (Landing Page)","2–3 Pages","4–5 Pages","6–10 Pages","10–20 Pages","20+ Pages"]} />
            </div>
            <div className="udash-form-row">
                <SelectField label="Preferred Platform / Tech" icon={faCode} name="platform" value={f.platform} onChange={hc} placeholder="— Select platform —" options={["WordPress","Shopify","React.js","Next.js","HTML/CSS/JS (Static)","Webflow","Custom / Backend","No Preference"]} />
                <SelectField label="Do You Have a Domain?" icon={faLink} name="hasDomain" value={f.hasDomain} onChange={hc} placeholder="— Select —" options={["Yes, I have a domain","No, I need help getting one","Not sure yet"]} />
            </div>
            <div className="udash-form-row">
                <SelectField label="Do You Have Hosting?" icon={faGlobe} name="hasHosting" value={f.hasHosting} onChange={hc} placeholder="— Select —" options={["Yes, I have hosting","No, I need hosting setup","Not sure yet"]} />
                <SelectField label="Deadline / Timeline" icon={faClock} name="deadline" value={f.deadline} onChange={hc} placeholder="— Select timeline —" options={["ASAP / Rush","Within 1 week","1–2 weeks","2–4 weeks","1–2 months","Flexible"]} />
            </div>
            <Field label="Reference Websites" icon={faLink}>
                <input type="text" name="referenceUrls" value={f.referenceUrls} onChange={hc} placeholder="e.g. https://example.com (sites you like)" />
            </Field>
            <div className="udash-form-row">
                <SelectField label="Do You Need a CMS?" icon={faEdit} name="needsCms" value={f.needsCms} onChange={hc} placeholder="— Select —" options={["Yes","No","Not sure"]} />
                <SelectField label="Do You Need SEO Setup?" icon={faStar} name="needsSeo" value={f.needsSeo} onChange={hc} placeholder="— Select —" options={["Yes, basic SEO","Yes, advanced SEO","No","Not sure"]} />
            </div>
            <Field label="Project Description & Requirements">
                <textarea name="description" value={f.description} onChange={hc} rows={4} placeholder="Describe your website goals, target audience, key features needed…" />
            </Field>
        </div>
    );
}

function LogoDesigningForm({ onFieldsChange }) {
    const [f, , hc] = useFormState({ fullName:'', email:'', brandName:'', tagline:'', industry:'', style:'', logoType:'', colorPref:'', brandColors:'', concepts:'', outputFormats:'', references:'', notes:'' });
    useEffect(() => onFieldsChange(f), [f]);
    return (
        <div className="udash-form-body">
            <SectionLabel>Client Details</SectionLabel>
            <div className="udash-form-row">
                <Field label="Full Name" icon={faUser}><input type="text" name="fullName" value={f.fullName} onChange={hc} placeholder="Your full name" /></Field>
                <Field label="Email Address" icon={faEnvelope}><input type="email" name="email" value={f.email} onChange={hc} placeholder="your@email.com" /></Field>
            </div>
            <SectionLabel>Logo Details</SectionLabel>
            <div className="udash-form-row">
                <Field label="Business / Brand Name" icon={faTag}><input type="text" name="brandName" value={f.brandName} onChange={hc} placeholder="e.g. Grand Indigo Studio" /></Field>
                <Field label="Tagline / Slogan (optional)" icon={faFont}><input type="text" name="tagline" value={f.tagline} onChange={hc} placeholder="e.g. Stitch. Design. Deliver." /></Field>
            </div>
            <div className="udash-form-row">
                <SelectField label="Industry / Niche" icon={faTag} name="industry" value={f.industry} onChange={hc} placeholder="— Select industry —" options={["Fashion / Apparel","Technology / SaaS","Food & Beverage","Health & Wellness","Real Estate","Finance / Banking","Education","Sports / Fitness","Beauty / Cosmetics","Retail / E-Commerce","Non-Profit","Other"]} />
                <SelectField label="Logo Style" icon={faStar} name="style" value={f.style} onChange={hc} placeholder="— Select style —" options={["Minimalist / Modern","Vintage / Retro","Bold / Strong","Playful / Fun","Luxury / Premium","Handwritten / Script","Geometric / Abstract","Illustrative","No Preference"]} />
            </div>
            <div className="udash-form-row">
                <SelectField label="Logo Type" icon={faImage} name="logoType" value={f.logoType} onChange={hc} placeholder="— Select type —" options={["Wordmark (text only)","Lettermark (initials)","Icon / Symbol only","Combination Mark (icon + text)","Emblem / Badge","No Preference"]} />
                <SelectField label="Color Preference" icon={faPalette} name="colorPref" value={f.colorPref} onChange={hc} placeholder="— Select preference —" options={["Black & White only","1–2 Colors","3–4 Colors","Colorful / Vibrant","I have specific brand colors","No Preference"]} />
            </div>
            <Field label="Brand Colors / Hex Codes (optional)" icon={faPalette}>
                <input type="text" name="brandColors" value={f.brandColors} onChange={hc} placeholder="e.g. #002536, Navy Blue, Gold" />
            </Field>
            <div className="udash-form-row">
                <SelectField label="Number of Concepts" icon={faStar} name="concepts" value={f.concepts} onChange={hc} placeholder="— Select —" options={["1 Concept","2 Concepts","3 Concepts"]} />
                <SelectField label="Output Formats Needed" icon={faFileAlt} name="outputFormats" value={f.outputFormats} onChange={hc} placeholder="— Select formats —" options={["PNG + JPG only","AI + EPS (Vector)","All formats (PNG, JPG, AI, EPS, SVG, PDF)","Custom"]} />
            </div>
            <Field label="Competitors / Reference Logos" icon={faLink}>
                <input type="text" name="references" value={f.references} onChange={hc} placeholder="Paste links to logos you like" />
            </Field>
            <Field label="Additional Details & Vision">
                <textarea name="notes" value={f.notes} onChange={hc} rows={3} placeholder="Describe your brand personality, target audience…" />
            </Field>
        </div>
    );
}

function BrandingsForm({ onFieldsChange }) {
    const [f, setF, hc] = useFormState({ fullName:'', email:'', brandName:'', phone:'', industry:'', brandStage:'', deliverables:[], brandTone:'', colorDirection:'', brandColors:'', competitors:'', brandStory:'' });
    useEffect(() => onFieldsChange(f), [f]);
    const toggleDeliverable = (item) => {
        setF('deliverables', f.deliverables.includes(item)
            ? f.deliverables.filter(d => d !== item)
            : [...f.deliverables, item]);
    };
    return (
        <div className="udash-form-body">
            <SectionLabel>Client Details</SectionLabel>
            <div className="udash-form-row">
                <Field label="Full Name" icon={faUser}><input type="text" name="fullName" value={f.fullName} onChange={hc} placeholder="Your full name" /></Field>
                <Field label="Email Address" icon={faEnvelope}><input type="email" name="email" value={f.email} onChange={hc} placeholder="your@email.com" /></Field>
            </div>
            <div className="udash-form-row">
                <Field label="Business / Brand Name" icon={faTag}><input type="text" name="brandName" value={f.brandName} onChange={hc} placeholder="e.g. Grand Indigo Studio" /></Field>
                <Field label="Phone Number" icon={faPhone}><input type="tel" name="phone" value={f.phone} onChange={hc} placeholder="+1 (555) 000-0000" /></Field>
            </div>
            <SectionLabel>Branding Package</SectionLabel>
            <div className="udash-form-row">
                <SelectField label="Industry / Niche" icon={faTag} name="industry" value={f.industry} onChange={hc} placeholder="— Select industry —" options={["Fashion / Apparel","Technology / SaaS","Food & Beverage","Health & Wellness","Real Estate","Finance / Banking","Education","Sports / Fitness","Beauty / Cosmetics","Retail / E-Commerce","Other"]} />
                <SelectField label="Brand Stage" icon={faStar} name="brandStage" value={f.brandStage} onChange={hc} placeholder="— Select stage —" options={["New Brand (starting from scratch)","Rebrand (updating existing identity)","Brand Extension (new product line)"]} />
            </div>
            <Field label="Branding Deliverables Needed" icon={faCheckCircle}>
                <div className="udash-checkbox-grid">
                    {["Logo Design","Color Palette","Typography Guide","Business Card","Letterhead","Email Signature","Social Media Kit","Brand Style Guide (PDF)","Favicon / App Icon","Packaging Design","Merchandise / Apparel Mockup","Presentation Template"].map(item => (
                        <label key={item} className="udash-checkbox-item">
                            <input type="checkbox" checked={f.deliverables.includes(item)} onChange={() => toggleDeliverable(item)} />
                            <span>{item}</span>
                        </label>
                    ))}
                </div>
            </Field>
            <div className="udash-form-row">
                <SelectField label="Brand Tone / Personality" icon={faStar} name="brandTone" value={f.brandTone} onChange={hc} placeholder="— Select tone —" options={["Professional & Trustworthy","Bold & Energetic","Elegant & Luxurious","Friendly & Approachable","Minimal & Clean","Playful & Creative","Edgy & Innovative"]} />
                <SelectField label="Color Direction" icon={faPalette} name="colorDirection" value={f.colorDirection} onChange={hc} placeholder="— Select preference —" options={["I have existing brand colors","I need colors suggested","Open to direction"]} />
            </div>
            <Field label="Brand Colors / Hex Codes (if applicable)" icon={faPalette}>
                <input type="text" name="brandColors" value={f.brandColors} onChange={hc} placeholder="e.g. #002536, #1d6a88, Gold" />
            </Field>
            <Field label="Competitor Brands / Inspiration Links" icon={faLink}>
                <input type="text" name="competitors" value={f.competitors} onChange={hc} placeholder="Brands you admire or want to stand out from" />
            </Field>
            <Field label="Brand Story & Goals">
                <textarea name="brandStory" value={f.brandStory} onChange={hc} rows={4} placeholder="Tell us about your brand…" />
            </Field>
        </div>
    );
}

const SERVICE_FORMS = {
    "Embroidery Digitizing": EmbroideryDigitizingForm,
    "Vectorization": VectorizationForm,
    "Website Development": WebsiteDevelopmentForm,
    "Logo Designing": LogoDesigningForm,
    "Brandings": BrandingsForm,
};

/* ─── Summary Tab ────────────────────────────────────────────────────────── */

function SummaryTab() {
    const [stats,   setStats]   = useState(null);
    const [recent,  setRecent]  = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');

    useEffect(() => {
        fetchDashboardStats()
            .then(data => { setStats(data.stats); setRecent(data.recent_orders || []); })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Spinner />;

    const statCards = [
        { icon: faClipboardList, label: "Total Orders",  value: stats?.total_orders  ?? 0, color: "#1d6a88" },
        { icon: faEdit,          label: "Total Edits",   value: stats?.total_edits   ?? 0, color: "#0e8a5f" },
        { icon: faLayerGroup,    label: "Active Edits",  value: stats?.active_edits  ?? 0, color: "#d97706" },
        { icon: faDollarSign,    label: "Amount Paid",   value: `$${Number(stats?.total_spent ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, color: "#7c3aed" },
    ];

    return (
        <div>
            <h3 className="udash-section-title"><FontAwesomeIcon icon={faTachometerAlt} /> Dashboard Summary</h3>
            <ErrorMsg msg={error} />
            <div className="udash-summary-grid">
                {statCards.map((s, i) => (
                    <div className="udash-stat-card" key={i} style={{ "--udash-stat-color": s.color }}>
                        <div className="udash-stat-icon"><FontAwesomeIcon icon={s.icon} /></div>
                        <div className="udash-stat-body">
                            <div className="udash-stat-value">{s.value}</div>
                            <div className="udash-stat-label">{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="udash-recent-orders">
                <h4 className="udash-sub-title">Recent Orders</h4>
                {recent.length === 0
                    ? <p style={{ color: '#8a99ad', fontSize: 14 }}>No orders yet.</p>
                    : (
                        <div className="udash-recent-list">
                            {recent.map(order => (
                                <div className="udash-recent-row" key={order.id}>
                                    <div className="udash-recent-service">
                                        <span className="udash-svc-emoji">{SERVICE_ICONS[order.service] || "📋"}</span>
                                        <div>
                                            <div className="udash-recent-name">{order.order_number}</div>
                                            <div className="udash-recent-svc">{order.service}</div>
                                        </div>
                                    </div>
                                    <div className="udash-recent-meta">
                                        <div className="udash-recent-price">
                                            {order.paid_amount != null
                                                ? `$${Number(order.paid_amount).toFixed(2)}`
                                                : <span style={{fontSize:12,color:'#a0aebb',fontWeight:600}}>Pending payment</span>
                                            }
                                        </div>
                                        <div className="udash-recent-date">{order.created_at_formatted}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                }
            </div>
        </div>
    );
}

/* ─── View Orders Tab ────────────────────────────────────────────────────── */

function ViewOrdersTab() {
    const [orders,   setOrders]   = useState([]);
    const [meta,     setMeta]     = useState({ total: 0, last_page: 1, current_page: 1 });
    const [page,     setPage]     = useState(1);
    const [currency, setCurrency] = useState("USD");
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState('');

    const load = useCallback((p) => {
        setLoading(true);
        fetchOrders(p, currency)
            .then(data => {
                setOrders(data.data);
                setMeta({ total: data.total, last_page: data.last_page, current_page: data.current_page });
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [currency]);

    useEffect(() => { load(page); }, [page, currency]);

    const activeCurrency = CURRENCIES.find(c => c.code === currency);
    const convertPrice = (usd) => currency === "GBP" ? (Number(usd) * USD_TO_GBP).toFixed(2) : Number(usd).toFixed(2);
    const start = (meta.current_page - 1) * 10 + 1;
    const end   = Math.min(meta.current_page * 10, meta.total);
    const pageNums = [];
    let from = Math.max(1, page - 2);
    let to   = Math.min(meta.last_page, from + 4);
    from = Math.max(1, to - 4);
    for (let i = from; i <= to; i++) pageNums.push(i);

    return (
        <div>
            <div className="udash-orders-header-row">
                <h3 className="udash-section-title" style={{ marginBottom: 0 }}>
                    <FontAwesomeIcon icon={faList} /> My Orders
                    <span className="udash-orders-count-badge">{meta.total}</span>
                </h3>
                <div className="udash-currency-switcher">
                    <span className="udash-currency-label">Currency:</span>
                    <div className="udash-currency-toggle">
                        {CURRENCIES.map(c => (
                            <button key={c.code} className={`udash-currency-btn${currency === c.code ? " active" : ""}`} onClick={() => setCurrency(c.code)}>
                                <span>{c.flag}</span> {c.code}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <ErrorMsg msg={error} />
            {loading ? <Spinner /> : (
                <>
                    <div className="udash-table-wrap">
                        <table className="udash-table">
                            <thead>
                                <tr><th>Order #</th><th>Service</th><th>Status</th><th>Payment</th><th>Amount ({activeCurrency.symbol})</th><th>Date</th><th>Invoice</th></tr>
                            </thead>
                            <tbody>
                                {orders.length === 0 ? (
                                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#8a99ad', padding: '40px' }}>No orders found.</td></tr>
                                ) : orders.map((order) => {
                                    const statusStyle = STATUS_STYLES[order.status] || STATUS_STYLES.pending;
                                    const pmtStyle = order.payment_status === 'paid'
                                        ? { bg: '#e6f9f0', color: '#0e8a5f', label: 'Paid' }
                                        : { bg: '#fff8e1', color: '#d97706', label: 'Unpaid' };
                                    const displayAmount = order.paid_amount != null ? order.paid_amount : order.amount;
                                    return (
                                        <tr key={order.id}>
                                            <td><div className="udash-order-num">{order.order_number}</div></td>
                                            <td><span className="udash-svc-pill"><span>{SERVICE_ICONS[order.service] || "📋"}</span>{order.service}</span></td>
                                            <td>
                                                <span className="udash-status-pill" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                                                    {statusStyle.label}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="udash-status-pill" style={{ background: pmtStyle.bg, color: pmtStyle.color }}>
                                                    {pmtStyle.label}
                                                </span>
                                                {order.paid_at && (
                                                    <div style={{ fontSize: 11, color: '#a0aebb', marginTop: 3 }}>{order.paid_at}</div>
                                                )}
                                            </td>
                                            <td>
                                                {displayAmount > 0
                                                    ? <div className="udash-td-price"><small>{currency}</small>{activeCurrency.symbol}{convertPrice(displayAmount)}</div>
                                                    : <div style={{ fontSize: 13, color: '#a0aebb' }}>—</div>
                                                }
                                            </td>
                                            <td><div className="udash-td-date"><FontAwesomeIcon icon={faClock} />{order.created_at_formatted}</div></td>
                                            <td className="udash-td-invoice-cell">
                                                <button className="udash-invoice-btn" disabled>
                                                    <FontAwesomeIcon icon={faFileInvoice} /> Invoice
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {meta.last_page > 1 && (
                        <div className="udash-pagination">
                            <div className="udash-pg-info">Showing {start}–{end} of {meta.total} orders</div>
                            <div className="udash-pg-controls">
                                <button className="udash-pg-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}><FontAwesomeIcon icon={faChevronLeft} /></button>
                                {pageNums.map(n => <button key={n} className={`udash-pg-btn${page === n ? " active" : ""}`} onClick={() => setPage(n)}>{n}</button>)}
                                <button className="udash-pg-btn" onClick={() => setPage(p => p + 1)} disabled={page === meta.last_page}><FontAwesomeIcon icon={faChevronRight} /></button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/* ─── Create Order Tab ───────────────────────────────────────────────────── */

function CreateOrderTab() {
    const [selectedService, setSelectedService] = useState(null);
    const [fields,          setFields]          = useState({});
    const [files,           setFiles]           = useState([]);
    const [submitting,      setSubmitting]      = useState(false);
    const [submitted,       setSubmitted]       = useState(false);
    const [error,           setError]           = useState('');

    const handleSubmit = async () => {
        setError('');
        if (!fields.fullName || !fields.email) { setError('Full name and email are required.'); return; }
        try {
            setSubmitting(true);
            await submitOrder(selectedService, fields, files);
            setSubmitted(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="udash-success-wrap">
                <div className="udash-success-icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
                <h3>Order Submitted!</h3>
                <p>Your order has been placed and a notification has been sent to our team. We'll be in touch shortly.</p>
                <button className="udash-btn-primary" onClick={() => { setSubmitted(false); setSelectedService(null); setFields({}); setFiles([]); }}>
                    Place Another Order
                </button>
            </div>
        );
    }

    if (selectedService) {
        const FormComponent = SERVICE_FORMS[selectedService];
        const fileId = `order-file-${selectedService.replace(/\s/g, '-').toLowerCase()}`;
        return (
            <div className="udash-service-form-wrap">
                <button className="udash-back-btn" type="button" onClick={() => { setSelectedService(null); setFields({}); setFiles([]); setError(''); }}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Back to Services
                </button>
                <div className="udash-form-header">
                    <span className="udash-form-emoji">{SERVICE_ICONS[selectedService]}</span>
                    <div>
                        <h4 className="udash-form-title">{selectedService}</h4>
                        <p className="udash-form-subtitle">Fill in the details below to place your order</p>
                    </div>
                </div>
                <ErrorMsg msg={error} />
                {FormComponent && <FormComponent onFieldsChange={setFields} />}
                <FileUploadField
                    label="Attach Files (optional)"
                    hint="Supports: JPG, PNG, PDF, AI, EPS, DST, PES — Max 20 MB each"
                    id={fileId}
                    files={files}
                    setFiles={setFiles}
                />
                <div className="udash-form-footer" style={{ marginTop: 24 }}>
                    <button className="udash-btn-secondary" type="button" onClick={() => { setSelectedService(null); setFields({}); setFiles([]); setError(''); }}>Cancel</button>
                    <button className="udash-btn-primary" type="button" onClick={handleSubmit} disabled={submitting}>
                        {submitting
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Submitting...</>
                            : <><FontAwesomeIcon icon={faCheckCircle} /> Submit Order</>
                        }
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h3 className="udash-section-title"><FontAwesomeIcon icon={faPlus} /> Create New Order</h3>
            <p className="udash-hint-text">Select a service to get started:</p>
            <div className="udash-service-grid">
                {SERVICES.map(svc => (
                    <button key={svc} className="udash-service-card" type="button" onClick={() => setSelectedService(svc)}>
                        <span className="udash-svc-big-emoji">{SERVICE_ICONS[svc]}</span>
                        <span className="udash-svc-name">{svc}</span>
                        <FontAwesomeIcon icon={faChevronRight} className="udash-svc-arrow" />
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ─── View Edits Tab ─────────────────────────────────────────────────────── */

function ViewEditsTab() {
    const [edits,   setEdits]   = useState([]);
    const [meta,    setMeta]    = useState({ total: 0, last_page: 1, current_page: 1 });
    const [page,    setPage]    = useState(1);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');

    useEffect(() => {
        setLoading(true);
        fetchEdits(page)
            .then(data => { setEdits(data.data); setMeta({ total: data.total, last_page: data.last_page, current_page: data.current_page }); })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [page]);

    const start = (meta.current_page - 1) * 10 + 1;
    const end   = Math.min(meta.current_page * 10, meta.total);
    const pageNums = [];
    let from = Math.max(1, page - 2);
    let to   = Math.min(meta.last_page, from + 4);
    from = Math.max(1, to - 4);
    for (let i = from; i <= to; i++) pageNums.push(i);

    const editStatusStyles = {
        pending:     { bg: "#fff8e1", color: "#d97706", label: "Pending"     },
        in_progress: { bg: "#e0f0ff", color: "#1d6a88", label: "In Progress" },
        completed:   { bg: "#e6f9f0", color: "#0e8a5f", label: "Completed"   },
    };

    return (
        <div>
            <div className="udash-orders-header-row">
                <h3 className="udash-section-title" style={{ marginBottom: 0 }}>
                    <FontAwesomeIcon icon={faHistory} /> My Edit Requests
                    <span className="udash-orders-count-badge">{meta.total}</span>
                </h3>
            </div>
            <ErrorMsg msg={error} />
            {loading ? <Spinner /> : (
                <>
                    <div className="udash-table-wrap">
                        <table className="udash-table">
                            <thead>
                                <tr><th>Edit #</th><th>Order</th><th>Instructions</th><th>Status</th><th>Files</th><th>Date</th></tr>
                            </thead>
                            <tbody>
                                {edits.length === 0 ? (
                                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#8a99ad', padding: '40px' }}>No edit requests yet.</td></tr>
                                ) : edits.map((edit) => {
                                    const s = editStatusStyles[edit.status] || editStatusStyles.pending;
                                    return (
                                        <tr key={edit.id}>
                                            <td><div className="udash-order-num">#{edit.id}</div></td>
                                            <td><div className="udash-td-name">{edit.order_number}</div><div className="udash-td-email">{edit.service}</div></td>
                                            <td><div className="udash-td-desc">{edit.instructions}</div></td>
                                            <td><span className="udash-status-pill" style={{ background: s.bg, color: s.color }}>{s.label}</span></td>
                                            <td><span style={{ fontSize: 13, color: '#8a99ad' }}>{edit.files_count ?? 0} file{edit.files_count !== 1 ? 's' : ''}</span></td>
                                            <td><div className="udash-td-date"><FontAwesomeIcon icon={faClock} />{edit.created_at_formatted}</div></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {meta.last_page > 1 && (
                        <div className="udash-pagination">
                            <div className="udash-pg-info">Showing {start}–{end} of {meta.total}</div>
                            <div className="udash-pg-controls">
                                <button className="udash-pg-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}><FontAwesomeIcon icon={faChevronLeft} /></button>
                                {pageNums.map(n => <button key={n} className={`udash-pg-btn${page === n ? " active" : ""}`} onClick={() => setPage(n)}>{n}</button>)}
                                <button className="udash-pg-btn" onClick={() => setPage(p => p + 1)} disabled={page === meta.last_page}><FontAwesomeIcon icon={faChevronRight} /></button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/* ─── Request Edit Tab ───────────────────────────────────────────────────── */

function RequestEditTab() {
    const [orders,       setOrders]       = useState([]);
    const [selectedOrder,setSelectedOrder]= useState('');
    const [instructions, setInstructions] = useState('');
    const [files,        setFiles]        = useState([]);
    const [submitting,   setSubmitting]   = useState(false);
    const [submitted,    setSubmitted]    = useState(false);
    const [error,        setError]        = useState('');

    useEffect(() => {
        // Load all orders (unpaginated) for the dropdown
        fetchOrders(1, 'USD').then(data => setOrders(data.data)).catch(() => {});
    }, []);

    const handleSubmit = async () => {
        setError('');
        if (!selectedOrder) { setError('Please select an order.'); return; }
        if (!instructions.trim()) { setError('Please describe the edits you need.'); return; }
        try {
            setSubmitting(true);
            await submitEdit(selectedOrder, instructions, files);
            setSubmitted(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="udash-success-wrap">
                <div className="udash-success-icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
                <h3>Edit Request Submitted!</h3>
                <p>We've received your edit request and will process it shortly.</p>
                <button className="udash-btn-primary" onClick={() => { setSubmitted(false); setSelectedOrder(''); setInstructions(''); setFiles([]); }}>
                    Submit Another Edit
                </button>
            </div>
        );
    }

    return (
        <div>
            <h3 className="udash-section-title"><FontAwesomeIcon icon={faEdit} /> Request an Edit</h3>
            <p className="udash-hint-text">Select your order and describe the changes you need.</p>
            <ErrorMsg msg={error} />
            <div className="udash-edits-form">
                <div className="udash-field">
                    <label>Select Order</label>
                    <div className="udash-select-wrap">
                        <select value={selectedOrder} onChange={e => setSelectedOrder(e.target.value)}>
                            <option value="" disabled>— Choose an order —</option>
                            {orders.map(o => (
                                <option key={o.id} value={o.id}>
                                    {o.order_number} — {o.service}
                                </option>
                            ))}
                        </select>
                        <FontAwesomeIcon icon={faChevronDown} className="udash-select-arrow" />
                    </div>
                </div>
                <div className="udash-field">
                    <label>Edit Instructions</label>
                    <textarea
                        rows={5}
                        value={instructions}
                        onChange={e => setInstructions(e.target.value)}
                        placeholder="Describe the changes you need in detail. Be as specific as possible…"
                    />
                </div>
                <FileUploadField
                    label={<>Attach Files <span className="udash-label-hint">(optional)</span></>}
                    hint="Supports: JPG, PNG, PDF, AI, EPS, DST, PES — Max 20 MB each"
                    id="udash-edit-file"
                    files={files}
                    setFiles={setFiles}
                />
                <div className="udash-form-footer" style={{ marginTop: 8 }}>
                    <button className="udash-btn-primary" type="button" onClick={handleSubmit} disabled={submitting}>
                        {submitting
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Submitting...</>
                            : <><FontAwesomeIcon icon={faEdit} /> Submit Edit Request</>
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Profile Tab ────────────────────────────────────────────────────────── */

function ProfileTab() {
    const navigate  = useNavigate();
    const storedUser = getUser() || {};

    const [profileData, setProfileData] = useState({
        first_name: storedUser.first_name || '',
        last_name:  storedUser.last_name  || '',
        email:      storedUser.email      || '',
        phone:      storedUser.phone      || '',
    });
    const [passData, setPassData] = useState({ current_password: '', password: '', password_confirmation: '' });
    const [showPass,    setShowPass]    = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [profileMsg,  setProfileMsg]  = useState({ type: '', text: '' });
    const [passMsg,     setPassMsg]     = useState({ type: '', text: '' });
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPass,    setSavingPass]    = useState(false);

    const initials = `${storedUser.first_name?.[0] || ''}${storedUser.last_name?.[0] || ''}`.toUpperCase() || '??';

    const handleSaveProfile = async () => {
        setProfileMsg({ type: '', text: '' });
        try {
            setSavingProfile(true);
            await updateProfile(profileData);
            setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
        } catch (e) {
            setProfileMsg({ type: 'error', text: e.message });
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePass = async () => {
        setPassMsg({ type: '', text: '' });
        if (!passData.current_password || !passData.password || !passData.password_confirmation) {
            setPassMsg({ type: 'error', text: 'Please fill in all password fields.' });
            return;
        }
        if (passData.password !== passData.password_confirmation) {
            setPassMsg({ type: 'error', text: 'New passwords do not match.' });
            return;
        }
        try {
            setSavingPass(true);
            await changePassword(passData);
            setPassMsg({ type: 'success', text: 'Password changed successfully.' });
            setPassData({ current_password: '', password: '', password_confirmation: '' });
        } catch (e) {
            setPassMsg({ type: 'error', text: e.message });
        } finally {
            setSavingPass(false);
        }
    };

    const handleLogout = () => { logout(); navigate('/login-signup'); };

    return (
        <div>
            <h3 className="udash-section-title"><FontAwesomeIcon icon={faUser} /> My Profile</h3>
            <div className="udash-profile-header">
                <div className="udash-profile-avatar">{initials}</div>
                <div>
                    <h4 className="udash-profile-name">{storedUser.name || 'Your Name'}</h4>
                    <p className="udash-profile-since">{storedUser.email}</p>
                </div>
                <button className="udash-btn-secondary" style={{ marginLeft: 'auto' }} onClick={handleLogout}>
                    Sign Out
                </button>
            </div>

            <p className="udash-sub-label">Personal Information</p>
            {profileMsg.type === 'success' && <SuccessMsg msg={profileMsg.text} />}
            {profileMsg.type === 'error'   && <ErrorMsg   msg={profileMsg.text} />}
            <div className="udash-form-row">
                <div className="udash-field">
                    <label><FontAwesomeIcon icon={faUser} /> First Name</label>
                    <input type="text" value={profileData.first_name} onChange={e => setProfileData(p => ({ ...p, first_name: e.target.value }))} />
                </div>
                <div className="udash-field">
                    <label><FontAwesomeIcon icon={faUser} /> Last Name</label>
                    <input type="text" value={profileData.last_name} onChange={e => setProfileData(p => ({ ...p, last_name: e.target.value }))} />
                </div>
            </div>
            <div className="udash-form-row" style={{ marginTop: 16 }}>
                <div className="udash-field">
                    <label><FontAwesomeIcon icon={faEnvelope} /> Email Address</label>
                    <input type="email" value={profileData.email} onChange={e => setProfileData(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="udash-field">
                    <label><FontAwesomeIcon icon={faPhone} /> Phone Number</label>
                    <input type="tel" value={profileData.phone} onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))} />
                </div>
            </div>
            <button className="udash-btn-primary" style={{ marginTop: 20 }} onClick={handleSaveProfile} disabled={savingProfile}>
                {savingProfile ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faPen} /> Save Profile</>}
            </button>

            <div className="udash-divider" />

            <p className="udash-sub-label">Change Password</p>
            {passMsg.type === 'success' && <SuccessMsg msg={passMsg.text} />}
            {passMsg.type === 'error'   && <ErrorMsg   msg={passMsg.text} />}
            <div className="udash-field" style={{ marginBottom: 16 }}>
                <label><FontAwesomeIcon icon={faLock} /> Current Password</label>
                <input type="password" placeholder="Enter current password" value={passData.current_password} onChange={e => setPassData(p => ({ ...p, current_password: e.target.value }))} />
            </div>
            <div className="udash-form-row">
                <div className="udash-field">
                    <label><FontAwesomeIcon icon={faLock} /> New Password</label>
                    <div className="udash-pass-wrap">
                        <input type={showPass ? "text" : "password"} placeholder="New password" value={passData.password} onChange={e => setPassData(p => ({ ...p, password: e.target.value }))} />
                        <button className="udash-pass-toggle" type="button" onClick={() => setShowPass(v => !v)}><FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} /></button>
                    </div>
                </div>
                <div className="udash-field">
                    <label><FontAwesomeIcon icon={faLock} /> Confirm Password</label>
                    <div className="udash-pass-wrap">
                        <input type={showConfirm ? "text" : "password"} placeholder="Confirm password" value={passData.password_confirmation} onChange={e => setPassData(p => ({ ...p, password_confirmation: e.target.value }))} />
                        <button className="udash-pass-toggle" type="button" onClick={() => setShowConfirm(v => !v)}><FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} /></button>
                    </div>
                </div>
            </div>
            <button className="udash-btn-primary" style={{ marginTop: 20 }} onClick={handleChangePass} disabled={savingPass}>
                {savingPass ? <><FontAwesomeIcon icon={faSpinner} spin /> Updating...</> : <><FontAwesomeIcon icon={faLock} /> Change Password</>}
            </button>
        </div>
    );
}

/* ─── Main Dashboard ─────────────────────────────────────────────────────── */

const NAV_ITEMS = [
    { id: "summary", label: "Summary", icon: faTachometerAlt },
    {
        id: "orders", label: "Orders", icon: faClipboardList,
        children: [
            { id: "view-orders",   label: "View Orders",   icon: faList  },
            { id: "create-order",  label: "Create Order",  icon: faPlus  },
        ],
    },
    {
        id: "edits-parent", label: "Edits", icon: faEdit,
        children: [
            { id: "view-edits",    label: "View Edits",    icon: faHistory },
            { id: "request-edit",  label: "Request Edit",  icon: faEdit    },
        ],
    },
    { id: "profile", label: "Profile", icon: faUser },
];

export default function UserDashboard() {
    const navigate = useNavigate();
    const user = getUser();

    const [activeView,        setActiveView]        = useState("summary");
    const [ordersOpen,        setOrdersOpen]        = useState(false);
    const [editsOpen,         setEditsOpen]         = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // Redirect if not logged in
    useEffect(() => {
        if (!user) navigate('/login-signup', { replace: true });
    }, []);

    if (!user) return null;

    const isOrdersActive = ["view-orders", "create-order"].includes(activeView);
    const isEditsActive  = ["view-edits",  "request-edit"].includes(activeView);

    const handleNav = (id) => {
        if (id === "orders")       { setOrdersOpen(o => !o); }
        else if (id === "edits-parent") { setEditsOpen(o => !o); }
        else { setActiveView(id); setMobileSidebarOpen(false); }
    };

    const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || user.name?.[0]?.toUpperCase() || '?';

    return (
        <>
            <style>{`
                /* ══════════════════════════════════════════════
                   SCOPED DASHBOARD STYLES — prefix: udash-
                ══════════════════════════════════════════════ */
                .udash-wrapper { background: #f0f4f8; min-height: 80vh; padding: 48px 0 72px; }
                .udash-layout  { display: flex; gap: 28px; align-items: flex-start; }

                /* Sidebar */
                .udash-sidebar { width: 260px; flex-shrink: 0; background: #fff; border-radius: 18px; box-shadow: 0 2px 20px rgba(0,0,0,0.07); border: 1px solid rgba(0,0,0,0.05); overflow: hidden; position: sticky; top: 100px; }
                .udash-sidebar-user { background: linear-gradient(135deg, var(--primary-color), #1d6a88); padding: 24px 14px 20px; display: flex; align-items: center; gap: 14px; }
                .udash-sidebar-avatar { width: 32px; height: 32px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; color: #fff; flex-shrink: 0; border: 2px solid rgba(255,255,255,0.35); }
                .udash-sidebar-info h5 { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: #fff; }
                .udash-sidebar-info p  { margin: 0; font-size: 11px; color: rgba(255,255,255,0.7); }
                .udash-nav { padding: 12px 0 16px; }
                .udash-nav-item { display: flex; align-items: center; gap: 11px; padding: 11px 20px; font-size: 14px; font-weight: 600; color: #4a5a6a; cursor: pointer; transition: all 0.2s ease; border: none; background: transparent; width: 100%; text-align: left; position: relative; font-family: inherit; }
                .udash-nav-item:hover { color: var(--primary-color); background: rgba(0,37,54,0.04); }
                .udash-nav-item.active { color: var(--primary-color); background: rgba(0,37,54,0.06); font-weight: 700; }
                .udash-nav-item.active::before { content: ""; position: absolute; left: 0; top: 20%; height: 60%; width: 3px; background: var(--primary-color); border-radius: 0 3px 3px 0; }
                .udash-nav-item svg:first-child { width: 16px; text-align: center; flex-shrink: 0; color: #1d6a88; }
                .udash-nav-chevron { margin-left: auto; font-size: 11px; color: #a0aebb; transition: transform 0.2s ease; }
                .udash-nav-chevron.open { transform: rotate(180deg); }
                .udash-nav-sub { overflow: hidden; max-height: 0; transition: max-height 0.3s ease; }
                .udash-nav-sub.open { max-height: 200px; }
                .udash-nav-sub-item { display: flex; align-items: center; gap: 10px; padding: 9px 20px 9px 44px; font-size: 13.5px; font-weight: 500; color: #5a6a7a; cursor: pointer; border: none; background: transparent; width: 100%; text-align: left; transition: all 0.2s ease; position: relative; font-family: inherit; }
                .udash-nav-sub-item:hover { color: var(--primary-color); background: rgba(0,37,54,0.04); }
                .udash-nav-sub-item.active { color: var(--primary-color); background: rgba(29,106,136,0.07); font-weight: 700; }
                .udash-nav-sub-item.active::before { content: ""; position: absolute; left: 0; top: 20%; height: 60%; width: 3px; background: #1d6a88; border-radius: 0 3px 3px 0; }

                /* Main */
                .udash-main { flex: 1; min-width: 0; background: #fff; border-radius: 18px; box-shadow: 0 2px 20px rgba(0,0,0,0.07); border: 1px solid rgba(0,0,0,0.05); padding: 36px 40px; }
                .udash-section-title { font-size: 20px; font-weight: 700; color: var(--primary-color); margin: 0 0 28px; display: flex; align-items: center; gap: 10px; }

                /* Banners */
                .udash-error-banner   { background: #fff1f0; border: 1.5px solid #ffc2be; border-radius: 10px; padding: 11px 16px; font-size: 13.5px; color: #c0392b; font-weight: 600; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
                .udash-success-banner { background: #e6f9f0; border: 1.5px solid #b0e8d0; border-radius: 10px; padding: 11px 16px; font-size: 13.5px; color: #0e8a5f; font-weight: 600; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }

                /* Stats */
                .udash-summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 36px; }
                .udash-stat-card { background: #f8fafc; border: 1.5px solid #eef1f5; border-radius: 14px; padding: 20px 18px; display: flex; align-items: center; gap: 14px; transition: transform 0.2s, box-shadow 0.2s; }
                .udash-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
                .udash-stat-icon { width: 46px; height: 46px; border-radius: 12px; background: color-mix(in srgb, var(--udash-stat-color) 12%, white); display: flex; align-items: center; justify-content: center; font-size: 18px; color: var(--udash-stat-color); flex-shrink: 0; }
                .udash-stat-value { font-size: 22px; font-weight: 800; color: var(--primary-color); line-height: 1; margin-bottom: 4px; }
                .udash-stat-label { font-size: 12px; color: #8a99ad; font-weight: 500; }
                .udash-sub-title { font-size: 15px; font-weight: 700; color: var(--primary-color); margin: 0 0 16px; }
                .udash-recent-orders { background: #f8fafc; border: 1.5px solid #eef1f5; border-radius: 14px; padding: 20px 24px; }
                .udash-recent-list { display: flex; flex-direction: column; }
                .udash-recent-row { display: flex; align-items: center; justify-content: space-between; padding: 13px 0; border-bottom: 1px solid #eef1f5; }
                .udash-recent-row:last-child { border-bottom: none; }
                .udash-recent-service { display: flex; align-items: center; gap: 12px; }
                .udash-svc-emoji { font-size: 22px; width: 36px; text-align: center; }
                .udash-recent-name { font-size: 13.5px; font-weight: 600; color: var(--primary-color); }
                .udash-recent-svc  { font-size: 12px; color: #8a99ad; margin-top: 2px; }
                .udash-recent-meta { text-align: right; }
                .udash-recent-price { font-size: 14px; font-weight: 700; color: var(--primary-color); }
                .udash-recent-date  { font-size: 11.5px; color: #a0aebb; margin-top: 2px; }

                /* Orders */
                .udash-orders-header-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
                .udash-orders-count-badge { background: #e6f4f9; color: #1d6a88; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 20px; }
                .udash-currency-switcher { display: flex; align-items: center; gap: 8px; }
                .udash-currency-label { font-size: 12px; font-weight: 600; color: #a0aebb; text-transform: uppercase; letter-spacing: 0.07em; }
                .udash-currency-toggle { display: flex; background: #f0f4f8; border-radius: 10px; padding: 4px; gap: 2px; border: 1.5px solid #e4edf4; }
                .udash-currency-btn { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border: none; background: transparent; border-radius: 7px; font-size: 13px; font-weight: 700; color: #7a8fa3; cursor: pointer; transition: all 0.2s ease; font-family: inherit; }
                .udash-currency-btn.active { background: var(--primary-color); color: #fff; box-shadow: 0 3px 10px rgba(0,37,54,0.2); }
                .udash-currency-btn:hover:not(.active) { background: #e0e9f0; color: var(--primary-color); }

                /* Table */
                .udash-table-wrap { overflow-x: auto; border-radius: 12px; border: 1.5px solid #eef1f5; }
                .udash-table { width: 100%; border-collapse: collapse; font-size: 14px; }
                .udash-table thead tr { background: #f4f8fb; border-bottom: 2px solid #e4edf4; }
                .udash-table thead th { padding: 13px 16px; text-align: left; font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #7a8fa3; white-space: nowrap; }
                .udash-table thead th:last-child { text-align: center; }
                .udash-table tbody tr { border-bottom: 1px solid #f0f4f8; transition: background 0.15s; }
                .udash-table tbody tr:last-child { border-bottom: none; }
                .udash-table tbody tr:hover { background: #f8fbfd; }
                .udash-table td { padding: 14px 16px; vertical-align: middle; color: #3a4a5c; }
                .udash-order-num  { font-size: 12px; font-weight: 700; color: #a0aebb; white-space: nowrap; }
                .udash-td-name    { font-weight: 600; color: var(--primary-color); font-size: 13.5px; white-space: nowrap; }
                .udash-td-email   { font-size: 12px; color: #8a99ad; margin-top: 2px; }
                .udash-svc-pill   { display: inline-flex; align-items: center; gap: 6px; background: #e6f4f9; color: #1d6a88; font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 6px; white-space: nowrap; }
                .udash-status-pill { display: inline-block; font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 20px; white-space: nowrap; }
                .udash-td-desc  { font-size: 13px; color: #5a6a7a; max-width: 240px; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
                .udash-td-price { font-size: 15px; font-weight: 800; color: var(--primary-color); white-space: nowrap; }
                .udash-td-price small { font-size: 11px; font-weight: 600; color: #a0aebb; display: block; }
                .udash-td-date  { font-size: 12.5px; color: #8a99ad; white-space: nowrap; display: flex; align-items: center; gap: 6px; }
                .udash-invoice-btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border: 1.5px solid #c8d7e3; border-radius: 8px; background: #fff; color: #1d6a88; font-size: 12.5px; font-weight: 600; cursor: not-allowed; opacity: 0.55; white-space: nowrap; font-family: inherit; }
                td.udash-td-invoice-cell { text-align: center; }

                /* Pagination */
                .udash-pagination { display: flex; align-items: center; justify-content: space-between; margin-top: 20px; flex-wrap: wrap; gap: 12px; }
                .udash-pg-info { font-size: 13px; color: #8a99ad; font-weight: 500; }
                .udash-pg-controls { display: flex; align-items: center; gap: 6px; }
                .udash-pg-btn { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px; border: 1.5px solid #dde4ec; background: #fff; font-size: 13px; font-weight: 600; color: #5a6a7a; cursor: pointer; transition: all 0.2s; font-family: inherit; }
                .udash-pg-btn:hover:not(:disabled):not(.active) { border-color: #1d6a88; color: #1d6a88; }
                .udash-pg-btn.active { background: var(--primary-color); border-color: var(--primary-color); color: #fff; }
                .udash-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }

                /* Service grid */
                .udash-hint-text { font-size: 14px; color: #7a8fa3; margin: -12px 0 24px; }
                .udash-service-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
                .udash-service-card { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 28px 16px 22px; background: #f8fafc; border: 1.5px solid #e4edf4; border-radius: 16px; cursor: pointer; transition: all 0.25s ease; text-align: center; position: relative; font-family: inherit; }
                .udash-service-card:hover { border-color: #1d6a88; background: #eaf4f9; transform: translateY(-3px); box-shadow: 0 8px 24px rgba(29,106,136,0.12); }
                .udash-svc-big-emoji { font-size: 36px; line-height: 1; }
                .udash-svc-name { font-size: 13.5px; font-weight: 700; color: var(--primary-color); }
                .udash-svc-arrow { font-size: 11px; color: #a0aebb; position: absolute; right: 14px; top: 50%; transform: translateY(-50%); }

                /* Forms */
                .udash-service-form-wrap { display: flex; flex-direction: column; }
                .udash-back-btn { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #1d6a88; background: none; border: none; cursor: pointer; padding: 0 0 20px; transition: color 0.2s; font-family: inherit; }
                .udash-back-btn:hover { color: var(--primary-color); }
                .udash-form-header { display: flex; align-items: center; gap: 16px; padding: 18px 20px; background: #f0f7fa; border: 1.5px solid #d6eaf2; border-radius: 12px; margin-bottom: 24px; }
                .udash-form-emoji  { font-size: 32px; }
                .udash-form-title  { font-size: 17px; font-weight: 700; color: var(--primary-color); margin: 0 0 2px; }
                .udash-form-subtitle { font-size: 13px; color: #7a8fa3; margin: 0; }
                .udash-form-body { display: flex; flex-direction: column; gap: 18px; }
                .udash-form-section-label { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #a0aebb; padding-bottom: 8px; border-bottom: 1.5px solid #eef1f5; margin-bottom: 2px; }
                .udash-checkbox-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; padding: 16px; background: #f8fafc; border: 1.5px solid #e0e7ee; border-radius: 10px; }
                .udash-checkbox-item { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: #4a5a6a; font-weight: 500; }
                .udash-checkbox-item input[type="checkbox"] { width: 15px; height: 15px; accent-color: var(--primary-color); flex-shrink: 0; cursor: pointer; }
                .udash-edits-form { display: flex; flex-direction: column; gap: 20px; }
                .udash-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
                .udash-field { display: flex; flex-direction: column; gap: 7px; }
                .udash-field label { font-size: 12.5px; font-weight: 600; color: #6b7a90; letter-spacing: 0.03em; display: flex; align-items: center; gap: 7px; }
                .udash-field label svg { color: #1d6a88; font-size: 11px; }
                .udash-label-hint { font-size: 11px; color: #a0aebb; font-weight: 400; }
                .udash-field input, .udash-field textarea { width: 100%; padding: 11px 16px; border: 1.5px solid #e0e7ee; border-radius: 10px; font-size: 14px; color: var(--primary-color); background: #f8fafc; outline: none; transition: border-color 0.2s, box-shadow 0.2s; box-sizing: border-box; font-weight: 500; resize: vertical; font-family: inherit; }
                .udash-field input:focus, .udash-field textarea:focus { border-color: #1d6a88; box-shadow: 0 0 0 3px rgba(29,106,136,0.1); background: #fff; }
                .udash-select-wrap { position: relative; }
                .udash-select-wrap select { width: 100%; padding: 11px 40px 11px 16px; border: 1.5px solid #e0e7ee; border-radius: 10px; font-size: 14px; color: var(--primary-color); background: #f8fafc; outline: none; appearance: none; font-weight: 500; transition: border-color 0.2s, box-shadow 0.2s; cursor: pointer; font-family: inherit; box-sizing: border-box; }
                .udash-select-wrap select:focus { border-color: #1d6a88; box-shadow: 0 0 0 3px rgba(29,106,136,0.1); background: #fff; }
                .udash-select-arrow { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); font-size: 12px; color: #a0aebb; pointer-events: none; }
                .udash-dropzone { border: 2px dashed #c8d9e6; border-radius: 12px; padding: 32px 24px; text-align: center; cursor: pointer; transition: all 0.2s ease; background: #f8fafc; }
                .udash-dropzone:hover { border-color: #1d6a88; background: #edf6fa; }
                .udash-dropzone-icon { font-size: 26px; color: #1d6a88; margin-bottom: 10px; opacity: 0.7; display: block; }
                .udash-dropzone-text { font-size: 14px; font-weight: 500; color: #5a6a7a; margin: 0 0 6px; }
                .udash-dropzone-text span { color: #1d6a88; font-weight: 700; text-decoration: underline; }
                .udash-dropzone-hint { font-size: 12px; color: #a0aebb; margin: 0; }
                .udash-file-list { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
                .udash-file-item { display: flex; align-items: center; gap: 10px; padding: 9px 14px; background: #f0f7fa; border-radius: 8px; border: 1px solid #d6eaf2; }
                .udash-file-icon { color: #1d6a88; font-size: 14px; }
                .udash-file-name { flex: 1; font-size: 13px; font-weight: 600; color: var(--primary-color); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .udash-file-size { font-size: 12px; color: #8a99ad; white-space: nowrap; }
                .udash-file-remove { background: none; border: none; cursor: pointer; color: #c0cdd8; font-size: 13px; padding: 0; transition: color 0.2s; flex-shrink: 0; }
                .udash-file-remove:hover { color: #e53e3e; }

                /* Profile */
                .udash-profile-header { display: flex; align-items: center; gap: 20px; margin-bottom: 28px; padding-bottom: 24px; border-bottom: 1.5px solid #eef1f5; flex-wrap: wrap; }
                .udash-profile-avatar { width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(135deg, #1d6a88, var(--primary-color)); display: flex; align-items: center; justify-content: center; font-size: 28px; color: #fff; font-weight: 700; flex-shrink: 0; box-shadow: 0 6px 20px rgba(29,106,136,0.3); }
                .udash-profile-name  { font-size: 18px; font-weight: 700; color: var(--primary-color); margin: 0 0 4px; }
                .udash-profile-since { font-size: 13px; color: #8a99ad; margin: 0; }
                .udash-pass-wrap { position: relative; }
                .udash-pass-wrap input { padding-right: 40px; }
                .udash-pass-toggle { position: absolute; right: 13px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #a0aebb; font-size: 14px; padding: 0; transition: color 0.2s; }
                .udash-pass-toggle:hover { color: #1d6a88; }
                .udash-sub-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #a0aebb; margin: 0 0 16px; }
                .udash-divider { height: 1.5px; background: #eef1f5; margin: 28px 0; }

                /* Buttons */
                .udash-btn-primary { display: inline-flex; align-items: center; gap: 9px; padding: 12px 28px; background: linear-gradient(135deg, #1d6a88, var(--primary-color)); color: #fff; border: none; border-radius: 10px; font-size: 14.5px; font-weight: 600; cursor: pointer; transition: all 0.25s ease; box-shadow: 0 4px 14px rgba(0,37,54,0.2); font-family: inherit; }
                .udash-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,37,54,0.28); }
                .udash-btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
                .udash-btn-secondary { display: inline-flex; align-items: center; gap: 9px; padding: 12px 24px; background: #fff; color: #5a6a7a; border: 1.5px solid #dde4ec; border-radius: 10px; font-size: 14.5px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; font-family: inherit; }
                .udash-btn-secondary:hover { border-color: #1d6a88; color: #1d6a88; }
                .udash-form-footer { display: flex; gap: 12px; justify-content: flex-end; padding-top: 16px; border-top: 1.5px solid #eef1f5; }

                /* Success */
                .udash-success-wrap { text-align: center; padding: 60px 24px; }
                .udash-success-icon { font-size: 56px; color: #0e8a5f; margin-bottom: 20px; }
                .udash-success-wrap h3 { font-size: 22px; font-weight: 700; color: var(--primary-color); margin: 0 0 10px; }
                .udash-success-wrap p { font-size: 14.5px; color: #7a8fa3; margin: 0 0 28px; }

                /* Stripe element wrap */
                .stripe-element-wrap { padding: 12px 16px; border: 1.5px solid #e0e7ee; border-radius: 10px; background: #f8fafc; transition: border-color 0.2s; }
                .stripe-element-wrap:focus-within { border-color: #1d6a88; box-shadow: 0 0 0 3px rgba(29,106,136,0.1); background: #fff; }

                /* Mobile */
                .udash-mobile-toggle { display: none; align-items: center; gap: 10px; padding: 12px 16px; background: var(--primary-color); color: #fff; border: none; border-radius: 12px; font-size: 14px; font-weight: 600; cursor: pointer; margin-bottom: 16px; width: 100%; font-family: inherit; }

                /* Responsive */
                @media (max-width: 1024px) { .udash-summary-grid { grid-template-columns: repeat(2, 1fr); } }
                @media (max-width: 768px) {
                    .udash-layout { flex-direction: column; gap: 0; }
                    .udash-sidebar { width: 100%; position: static; display: none; border-radius: 0 0 18px 18px; margin-bottom: 16px; }
                    .udash-sidebar.mobile-open { display: block; }
                    .udash-mobile-toggle { display: flex; }
                    .udash-main { padding: 24px 18px; }
                    .udash-summary-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
                    .udash-form-row { grid-template-columns: 1fr; }
                    .udash-orders-header-row { flex-direction: column; align-items: flex-start; }
                    .udash-service-grid { grid-template-columns: repeat(2, 1fr); }
                    .udash-checkbox-grid { grid-template-columns: 1fr 1fr; }
                }
                @media (max-width: 480px) {
                    .udash-summary-grid { grid-template-columns: 1fr; }
                    .udash-service-grid { grid-template-columns: 1fr; }
                    .udash-checkbox-grid { grid-template-columns: 1fr; }
                }
            `}</style>

            <Header />
            <section className="breadcrumbsection">
                <div className="container">
                    <h1>User Dashboard</h1>
                    <ul className="breadcrumblinks">
                        <li><Link to="/">Home</Link></li>
                        <li><FontAwesomeIcon icon={faChevronRight} /></li>
                        <li>User Dashboard</li>
                    </ul>
                </div>
            </section>

            <div className="udash-wrapper">
                <div className="container">
                    <button className="udash-mobile-toggle" onClick={() => setMobileSidebarOpen(o => !o)}>
                        <FontAwesomeIcon icon={faUser} />
                        Dashboard Menu
                        <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: "auto", transform: mobileSidebarOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                    </button>

                    <div className="udash-layout">
                        {/* Sidebar */}
                        <aside className={`udash-sidebar${mobileSidebarOpen ? " mobile-open" : ""}`}>
                            <div className="udash-sidebar-user">
                                <div className="udash-sidebar-avatar">{initials}</div>
                                <div className="udash-sidebar-info">
                                    <h5>{user.name || `${user.first_name} ${user.last_name}`}</h5>
                                    <p>{user.email}</p>
                                </div>
                            </div>
                            <nav className="udash-nav">
                                {NAV_ITEMS.map(item => {
                                    const isParentActive = (item.id === "orders" && isOrdersActive) || (item.id === "edits-parent" && isEditsActive) || activeView === item.id;
                                    const isOpen = (item.id === "orders" && (ordersOpen || isOrdersActive)) || (item.id === "edits-parent" && (editsOpen || isEditsActive));
                                    return (
                                        <React.Fragment key={item.id}>
                                            <button className={`udash-nav-item${isParentActive ? " active" : ""}`} onClick={() => handleNav(item.id)}>
                                                <FontAwesomeIcon icon={item.icon} />
                                                {item.label}
                                                {item.children && <FontAwesomeIcon icon={faChevronDown} className={`udash-nav-chevron${isOpen ? " open" : ""}`} />}
                                            </button>
                                            {item.children && (
                                                <div className={`udash-nav-sub${isOpen ? " open" : ""}`}>
                                                    {item.children.map(child => (
                                                        <button key={child.id} className={`udash-nav-sub-item${activeView === child.id ? " active" : ""}`}
                                                            onClick={() => { setActiveView(child.id); setMobileSidebarOpen(false); }}>
                                                            <FontAwesomeIcon icon={child.icon} />
                                                            {child.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </nav>
                        </aside>

                        {/* Main content */}
                        <main className="udash-main">
                            {activeView === "summary"      && <SummaryTab />}
                            {activeView === "view-orders"  && <ViewOrdersTab />}
                            {activeView === "create-order" && <CreateOrderTab />}
                            {activeView === "view-edits"   && <ViewEditsTab />}
                            {activeView === "request-edit" && <RequestEditTab />}
                            {activeView === "profile"      && <ProfileTab />}
                        </main>
                    </div>
                </div>
            </div>

            <Newsletter />
            <Footer />
        </>
    );
}